import React from 'react';
import Button from 'components/Button';
import Spacing from 'components/Spacing';
import Heading from 'components/Heading';
import {
  Typography,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/ReportProblemOutlined';
import { Row } from 'components/Layout';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    maxWidth: '560px',
  },
});

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  color: theme.palette.grey[500],
  right: theme.spacing(1),
  top: theme.spacing(1),
}));

const ContentWrapper = styled(DialogContent)({
  paddingBottom: 0,
});

const DialogIcon = styled(WarningIcon)(({ theme }) => ({
  color: theme.palette.warning.main,
  fontSize: 65,
  margin: '0 auto',
  display: 'block',
}));

const Content = styled('div')(({ theme }) => ({
  fontSize: theme.fontSizes?.lg,
  color: theme.palette.text.secondary,
  letterSpacing: 0,
  textAlign: 'center',
}));

const Actions = styled(DialogActions)(({ theme }) => ({
  justifyContent: 'center',
  padding: theme.spacing(0, 6, 6),
  gap: theme.spacing(2),
  flexDirection: 'row',
  marginTop: theme.spacing(6),
}));

const ClickableLink = styled('button')(({ theme, disabled }) => ({
  cursor: disabled ? 'not-allowed' : 'pointer',
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: disabled ? theme.palette.text.disabled : theme.palette.primary.main,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.fontSizes?.lg,
}));

const RightMarginButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onDeleteSingle: () => void;
  onDeleteBulk: () => void;
  onDownloadFile: () => void;
  isDeleting: boolean;
  isDownloadingBulkUploadFile: boolean;
};

const CustomDeleteWithdrawalModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onDeleteSingle,
  onDeleteBulk,
  onDownloadFile,
  isDeleting,
  isDownloadingBulkUploadFile,
}) => {
  if (!isOpen) return null;

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      fullWidth
    >
      <CloseButton aria-label="close" onClick={onClose} size="large">
        <CloseIcon />
      </CloseButton>

      <ContentWrapper>
        <Spacing>
          <DialogIcon />
          <DialogContentText component="div">
            <Heading variant="h1" centered noMargin>
              <Row spacing="xs" centered>
                Deleting withdrawal record
              </Row>
            </Heading>
          </DialogContentText>
          <Content>
            <Spacing>
              <Typography variant="body1">
                This investor was part of a bulk withdrawal upload. You can
                delete this investor's entry or you can delete all entries in
                that bulk upload. Deletion cannot be undone. To download a
                record of the investor's bulk upload,{' '}
                <ClickableLink
                  disabled={isDownloadingBulkUploadFile}
                  onClick={(e) => {
                    e.preventDefault();
                    onDownloadFile();
                  }}
                >
                  click here
                </ClickableLink>
                .
              </Typography>
            </Spacing>
          </Content>
        </Spacing>
      </ContentWrapper>
      <Actions>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          disabled={isDeleting}
        >
          Go back
        </Button>
        <RightMarginButton
          color="primary"
          variant="contained"
          onClick={onDeleteSingle}
          disabled={isDeleting}
          loading={isDeleting}
        >
          Delete single record
        </RightMarginButton>
        <Button
          color="error"
          variant="contained"
          onClick={onDeleteBulk}
          disabled={isDeleting}
          loading={isDeleting}
        >
          Delete bulk upload
        </Button>
      </Actions>
    </StyledDialog>
  );
};

export default CustomDeleteWithdrawalModal;
